import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, updateDoc, doc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import moment from "moment";
import { db } from "../firebaseConfig";
import "./AddItem.css";

const AddItem = () => {
  const [newItem, setNewItem] = useState({
    siteName: "",
    startDate: "",
    jobDescription: "",
    techAssign: "",
    status: "Active",
    fileUpload: null,
  });
  const [addedItems, setAddedItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [showAddItemPopup, setShowAddItemPopup] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [techOptions, setTechOptions] = useState([]);
  const [createdBy, setCreatedBy] = useState("");
  const [propertiesList, setPropertiesList] = useState([]); // List of properties
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        if (userDoc) {
          setNewItem((prev) => ({
            ...prev,
            techAssign: `${userDoc.data().firstName} ${userDoc.data().lastName}`,
          }));
          setCreatedBy(`${userDoc.data().firstName} ${userDoc.data().lastName}`);
        }

        const techOptions = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: `${doc.data().firstName} ${doc.data().lastName}`,
        }));
        setTechOptions(techOptions);
      }
    };

    const fetchAddedItems = async () => {
      const jobsRef = collection(db, "jobs");
      const querySnapshot = await getDocs(jobsRef);
      const itemsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAddedItems(itemsData.filter(item => item.status !== "Completed" && item.status !== "Invoice Sent"));
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, 'properties'); // Fetch from 'properties' collection
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPropertiesList(propertiesList);
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };

    fetchUserData();
    fetchAddedItems();
    fetchProperties(); // Fetch properties on component mount
  }, []);

  // Sort propertiesList alphabetically by propertyName
  const sortedPropertiesList = propertiesList.sort((a, b) => a.propertyName.localeCompare(b.propertyName));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    setSelectedFiles(Array.from(e.target.files)); // Update to handle multiple files
  };

  const uploadFiles = async (files, path) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `${path}/${file.name}`);
      await uploadBytes(storageRef, file);
      const fileURL = await getDownloadURL(storageRef);
      return { fileURL, fileName: file.name };
    });
    return await Promise.all(uploadPromises);
  };

  const handleAddItem = async () => {
    setUploading(true);
    let uploadedFiles = [];

    if (selectedFiles.length > 0) {
      uploadedFiles = await uploadFiles(selectedFiles, 'uploads');
    }

    try {
      const jobsRef = collection(db, "jobs");
      await addDoc(jobsRef, { ...newItem, fileUpload: uploadedFiles, createdBy });
      setNewItem({
        siteName: "",
        startDate: "",
        jobDescription: "",
        techAssign: "",
        status: "Active",
        fileUpload: null,
      });
      setSelectedFiles([]);
      alert("Item added successfully!");
      setShowAddItemPopup(false);
      fetchAddedItems();
    } catch (error) {
      console.error("Error adding item:", error);
    } finally {
      setUploading(false);
    }
  };

  const fetchAddedItems = async () => {
    const jobsRef = collection(db, "jobs");
    const querySnapshot = await getDocs(jobsRef);
    const itemsData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setAddedItems(itemsData.filter(item => item.status !== "Completed" && item.status !== "Invoice Sent"));
  };

  const closeModal = () => setSelectedItem(null);

  const markItemCompleted = async () => {
    if (selectedItem) {
      const itemDoc = doc(db, "jobs", selectedItem.id);
      await updateDoc(itemDoc, { status: "Completed" });
      setAddedItems((prevItems) => prevItems.filter((item) => item.id !== selectedItem.id));
      closeModal();
      setShowVerificationPopup(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files)); // Update to handle multiple files
  };

  const saveNote = async () => {
    if (selectedItem && (noteText.trim() !== "" || selectedFiles.length > 0)) {
      setUploading(true);
      let uploadedFiles = [];

      if (selectedFiles.length > 0) {
        uploadedFiles = await uploadFiles(selectedFiles, `notes/${selectedItem.id}`);
      }

      const itemDoc = doc(db, "jobs", selectedItem.id);
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: createdBy,
        files: uploadedFiles,
      };

      const updatedNotes = Array.isArray(selectedItem.notes) ? [...selectedItem.notes, newNote] : [newNote];
      await updateDoc(itemDoc, { notes: updatedNotes });

      setSelectedItem((prev) => ({ ...prev, notes: updatedNotes }));
      setNoteText("");
      setSelectedFiles([]);
      setUploading(false);
      setShowNotePopup(false); // Close the Add Note window
    }
  };

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, 'days');

    if (diffDays > 30) {
      return 'red';
    } else if (diffDays > 7) {
      return 'orange';
    } else {
      return 'white';
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const SortButton = ({ columnKey, sortConfig, onSort }) => (
    <button onClick={() => onSort(columnKey)}>
      {sortConfig.key === columnKey ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : '↕'}
    </button>
  );

  return (
    <div className="add-item-page">
      <h1>Extra Jobs</h1>
      <button className="blue-button" onClick={() => setShowAddItemPopup(true)}>Click Here to Add New Item</button>

      {showAddItemPopup && (
        <>
          <div className="modal-overlay" onClick={() => setShowAddItemPopup(false)}></div>
          <div className="modal">
            <h2>Add New Item</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Site Name</label>
                <select name="siteName" onChange={handleInputChange} value={newItem.siteName} required>
                  <option value="">Select Site Name</option>
                  {sortedPropertiesList.map(property => (
                    <option key={property.id} value={property.propertyName}>{property.propertyName}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Date</label>
                <input type="date" name="startDate" onChange={handleInputChange} value={newItem.startDate} />
              </div>
              <div className="form-field">
                <label>Job Description</label>
                <textarea name="jobDescription" onChange={handleInputChange} value={newItem.jobDescription} />
              </div>
              <div className="form-field">
                <label>Tech</label>
                <select name="techAssign" onChange={handleInputChange} value={newItem.techAssign}>
                  {techOptions.map((tech) => (
                    <option key={tech.id} value={tech.name}>{tech.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>File Upload / Take Picture</label>
                <input type="file" accept=".png, .gif, .jpg, .jpeg, .hic, .pdf, .xlxs" capture="environment" multiple onChange={handleFileUpload} /> {/* Allow multiple file selection */}
              </div>
            </div>
            <div className="modal-buttons">
              <button className="green-button" onClick={handleAddItem} disabled={uploading}>{uploading ? "Uploading..." : "Add Item"}</button>
              <button className="red-button" onClick={() => setShowAddItemPopup(false)}>Close</button>
            </div>
          </div>
        </>
      )}

      <h2>Added Items</h2>
      {addedItems.length > 0 ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>
                  Site Name <SortButton columnKey="siteName" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>
                  City <SortButton columnKey="city" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>
                  Start Date <SortButton columnKey="startDate" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>
                  Service Type <SortButton columnKey="serviceType" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>Scope</th>
                <th>
                  Tech <SortButton columnKey="techAssign" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortData(addedItems, sortConfig.key, sortConfig.direction).map((item) => (
                <tr key={item.id} style={{ backgroundColor: getBackgroundColor(item.startDate) }}>
                  <td>{item.siteName}</td>
                  <td>{item.city}</td>
                  <td>{item.startDate}</td>
                  <td>{item.serviceType}</td>
                  <td>{item.scope}</td>
                  <td>{item.techAssign}</td>
                  <td>
                    <button className="blue-button" onClick={() => setSelectedItem(item)}>Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No items added yet.</p>
      )}

      {selectedItem && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal">
            <h2>Item Details</h2>
            <p><strong>Location:</strong> {selectedItem.siteName}</p>
            <p><strong>Date:</strong> {selectedItem.startDate}</p>
            <p><strong>Tech Assigned:</strong> {selectedItem.techAssign}</p>
            <p><strong>Created By:</strong> {selectedItem.createdBy}</p>
            <p><strong>Status:</strong> <span className="status">{capitalizeFirstLetter(selectedItem.status)}</span></p>
            <h3>Notes</h3>
            <div className="notes-container">
              {Array.isArray(selectedItem.notes) && selectedItem.notes.length > 0 ? (
                <ul>
                  {selectedItem.notes.map((note, index) => (
                    <li key={index}>
                      <strong>{note.author} ({note.timestamp}):</strong> {note.text}
                      {note.files && note.files.map((file, fileIndex) => (
                        <p key={fileIndex}>
                          <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
                            View Attachment
                          </a>
                        </p>
                      ))}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No notes yet.</p>
              )}
            </div>
            {selectedItem.fileUpload && Array.isArray(selectedItem.fileUpload) && (
              <div>
                <h3>Uploaded Files</h3>
                {selectedItem.fileUpload.map((file, index) => (
                  <p key={index}>
                    <a href={file.fileURL} target="_blank" rel="noopener noreferrer">
                      View Uploaded File
                    </a>
                  </p>
                ))}
              </div>
            )}
            <div className="modal-buttons">
              <button className="blue-button" onClick={() => setShowNotePopup(true)}>Add Note</button>
              <button className="green-button" onClick={() => setShowVerificationPopup(true)}>Mark Job as Completed</button>
              <button className="red-button" onClick={closeModal}>Close</button>
            </div>
          </div>
        </>
      )}

      {showNotePopup && (
        <>
          <div className="modal-overlay" onClick={() => setShowNotePopup(false)}></div>
          <div className="modal">
            <h2>Add Note</h2>
            <textarea rows="4" placeholder="Enter your note here..." value={noteText} onChange={(e) => setNoteText(e.target.value)}></textarea>
            <input type="file" accept=".png, .gif, .jpg, .jpeg, .hic, .pdf, .xlxs" multiple onChange={handleFileChange} /> {/* Allow multiple file selection */}
            <div className="modal-buttons">
              <button className="green-button" onClick={saveNote} disabled={uploading}>{uploading ? "Uploading..." : "Save"}</button>
              <button className="red-button" onClick={() => setShowNotePopup(false)}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {showVerificationPopup && (
        <>
          <div className="modal-overlay" onClick={() => setShowVerificationPopup(false)}></div>
          <div className="modal">
            <h3>Are you sure to mark job as completed?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={markItemCompleted}>Yes</button>
              <button className="shortred-button" onClick={() => setShowVerificationPopup(false)}>No</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddItem;